import { useState, useEffect } from "react";
import { Reorder } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, LogOut, Loader2, Film, GripVertical, Trash2, Plus, Save, Image } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";

export const AdminPanel = () => {
  const { isAdmin, login, logout, uploadAd, ads, deleteAd, reorderAds, socials, updateSocials, setAdThumbnail } = useAdmin();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [file, setFile] = useState(null);
  const [poster, setPoster] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [list, setList] = useState(ads);
  const [socialList, setSocialList] = useState([]);
  const [savingSocials, setSavingSocials] = useState(false);
  const [thumbBusy, setThumbBusy] = useState(null);

  useEffect(() => { setList(ads); }, [ads]);
  useEffect(() => { setSocialList(socials); }, [socials]);

  // Open the admin panel only via the secret URL hash "#admin"
  useEffect(() => {
    const check = () => {
      if (window.location.hash.toLowerCase() === "#admin") setOpen(true);
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, []);

  const closePanel = (v) => {
    setOpen(v);
    if (!v && window.location.hash.toLowerCase() === "#admin") {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  const handleLogin = async () => {
    if (loggingIn) return;
    setLoggingIn(true);
    try {
      const ok = await login(code);
      if (ok) toast.success("Admin unlocked");
      else toast.error("Wrong passcode");
    } finally {
      setLoggingIn(false);
      setCode("");
    }
  };

  const commitOrder = async () => {
    const ids = list.map((a) => a.id);
    if (ids.join() === ads.map((a) => a.id).join()) return;
    try {
      await reorderAds(ids);
      toast.success("Order saved");
    } catch {
      toast.error("Could not save order");
    }
  };

  const setSocial = (i, key, val) =>
    setSocialList((l) => l.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addSocial = () => setSocialList((l) => [...l, { label: "", href: "" }]);
  const removeSocial = (i) => setSocialList((l) => l.filter((_, idx) => idx !== i));
  const saveSocials = async () => {
    setSavingSocials(true);
    try {
      await updateSocials(socialList.filter((s) => s.label.trim() && s.href.trim()));
      toast.success("Social links saved");
    } catch {
      toast.error("Could not save social links");
    } finally {
      setSavingSocials(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!title || !file) return toast.error("Add a title and pick a video file");
    setProgress(0);
    try {
      await uploadAd({ title, subtitle, file, poster, onProgress: setProgress });
      toast.success("Ad uploaded");
      setTitle(""); setSubtitle(""); setFile(null); setPoster(null);
    } catch {
      toast.error("Upload failed");
    } finally {
      setProgress(null);
    }
  };

  const uploadRowThumb = async (id, f) => {
    if (!f) return;
    setThumbBusy(id);
    try {
      await setAdThumbnail(id, f);
      toast.success("Thumbnail updated");
    } catch {
      toast.error("Thumbnail upload failed");
    } finally {
      setThumbBusy(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={closePanel}>
        <DialogContent className="border-white/10 bg-[#0b0b0b] text-white sm:max-w-lg" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-anton text-2xl tracking-tightest">
              {isAdmin ? "MANAGE ADS" : "ADMIN ACCESS"}
            </DialogTitle>
          </DialogHeader>

          {!isAdmin ? (
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-white/60">Passcode</Label>
                <Input
                  type="password"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder="Enter admin passcode"
                  className="mt-2 border-white/15 bg-black/40 text-white"
                  data-testid="admin-passcode-input"
                />
              </div>
              <Button onClick={handleLogin} disabled={loggingIn} className="w-full bg-white text-black hover:bg-white/80" data-testid="admin-unlock-btn">
                {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
              </Button>
            </div>
          ) : (
            <div className="space-y-6 pt-2">
              <form onSubmit={handleUpload} className="space-y-4" data-testid="ad-upload-form">
                <div>
                  <Label className="text-white/60">Title</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Summer Launch"
                    className="mt-2 border-white/15 bg-black/40 text-white" data-testid="ad-title-input" />
                </div>
                <div>
                  <Label className="text-white/60">Subtitle (optional)</Label>
                  <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="e.g. Brand Film · 16:9"
                    className="mt-2 border-white/15 bg-black/40 text-white" data-testid="ad-subtitle-input" />
                </div>
                <div>
                  <Label className="text-white/60">Video file</Label>
                  <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-white/20 bg-black/40 px-4 py-4 text-sm text-white/70 hover:border-white/40">
                    <Film className="h-5 w-5" />
                    {file ? file.name : "Choose a video (mp4, mov, webm)…"}
                    <input type="file" accept="video/*" className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ad-file-input" />
                  </label>
                </div>
                <div>
                  <Label className="text-white/60">Thumbnail image (optional)</Label>
                  <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-white/20 bg-black/40 px-4 py-4 text-sm text-white/70 hover:border-white/40">
                    <Image className="h-5 w-5" />
                    {poster ? poster.name : "Choose a thumbnail (jpg, png, webp)…"}
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => setPoster(e.target.files?.[0] || null)} data-testid="ad-poster-input" />
                  </label>
                </div>
                <Button type="submit" disabled={progress !== null}
                  className="flex w-full items-center justify-center gap-2 bg-white text-black hover:bg-white/80" data-testid="ad-upload-btn">
                  {progress !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading {progress}%</> : <><Upload className="h-4 w-4" /> Upload ad</>}
                </Button>
              </form>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-white/40">Current ads ({list.length}) · drag to reorder</p>
                <Reorder.Group axis="y" values={list} onReorder={setList} className="space-y-2" data-testid="ads-reorder-group">
                  {list.map((a) => (
                    <Reorder.Item
                      key={a.id}
                      value={a}
                      onDragEnd={commitOrder}
                      className="flex cursor-grab items-center gap-3 rounded-md border border-white/10 bg-black/30 px-3 py-2 active:cursor-grabbing"
                      data-testid={`ad-row-${a.id}`}
                    >
                      <GripVertical className="h-4 w-4 shrink-0 text-white/40" />
                      <span className="flex-1 truncate text-sm text-white/80">
                        {a.title}{a.uploaded ? "" : " (default)"}
                      </span>
                      <label
                        className="flex cursor-pointer items-center text-white/50 hover:text-white"
                        title="Set thumbnail"
                        data-testid={`ad-thumb-${a.id}`}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        {thumbBusy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => uploadRowThumb(a.id, e.target.files?.[0])} />
                      </label>
                      <button
                        onClick={() => deleteAd(a.id)}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="text-red-300 hover:text-red-200"
                        data-testid={`admin-delete-${a.id}`}
                        aria-label="Delete ad"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              </div>

              <div className="space-y-3 border-t border-white/10 pt-5" data-testid="socials-editor">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40">Social links</p>
                  <button onClick={addSocial} className="flex items-center gap-1 text-xs text-white/60 hover:text-white" data-testid="social-add-btn">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </button>
                </div>
                {socialList.map((s, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`social-row-${i}`}>
                    <Input value={s.label} onChange={(e) => setSocial(i, "label", e.target.value)} placeholder="Label"
                      className="w-1/3 border-white/15 bg-black/40 text-white" data-testid={`social-label-${i}`} />
                    <Input value={s.href} onChange={(e) => setSocial(i, "href", e.target.value)} placeholder="https://…"
                      className="flex-1 border-white/15 bg-black/40 text-white" data-testid={`social-href-${i}`} />
                    <button onClick={() => removeSocial(i)} className="text-red-300 hover:text-red-200" data-testid={`social-remove-${i}`} aria-label="Remove link">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Button onClick={saveSocials} disabled={savingSocials}
                  className="flex w-full items-center justify-center gap-2 bg-white/10 text-white hover:bg-white/20" data-testid="social-save-btn">
                  {savingSocials ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save social links</>}
                </Button>
              </div>

              <Button variant="ghost" onClick={logout} className="flex w-full items-center justify-center gap-2 text-white/60 hover:text-white" data-testid="admin-logout-btn">
                <LogOut className="h-4 w-4" /> Lock admin
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
